import React from 'react';
import Icon from '../../../components/AppIcon';

const EMPLOYMENT_OPTIONS = [
  { value: 'employed', label: 'Employed (Full-time / Part-time)' },
  { value: 'self_employed', label: 'Self-Employed' },
  { value: 'creator_influencer', label: 'Creator / Influencer' },
  { value: 'student', label: 'Student' },
  { value: 'other', label: 'Other' },
];

const INCOME_OPTIONS = [
  { value: 'under_15k', label: 'Under $15,000' },
  { value: '15k_30k', label: '$15,000 – $30,000' },
  { value: '30k_60k', label: '$30,000 – $60,000' },
  { value: '60k_100k', label: '$60,000 – $100,000' },
  { value: 'over_100k', label: 'Over $100,000' },
];

const inputStyle = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  color: '#fff',
  padding: '10px 14px',
  width: '100%',
  fontSize: '14px',
  outline: 'none',
  transition: 'border-color 0.2s',
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '500',
  color: 'rgba(255,255,255,0.7)',
  marginBottom: '6px',
};

const errorStyle = {
  fontSize: '12px',
  color: '#f87171',
  marginTop: '4px',
};

const FormField = ({ label, required, error, children }) => (
  <div>
    <label style={labelStyle}>
      {label} {required && <span style={{ color: '#f87171' }}>*</span>}
    </label>
    {children}
    {error && <p style={errorStyle}>{error}</p>}
  </div>
);

const PersonalInfoStep = ({ data, onChange, errors }) => {
  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const handleAddressChange = (field, value) => {
    onChange({ ...data, address: { ...data?.address, [field]: value } });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Personal Information
        </h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
          Provide your legal details exactly as they appear on your ID documents.
        </p>
      </div>
      {/* Name + DOB */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Full Legal Name" required error={errors?.fullName}>
          <input
            type="text"
            placeholder="As on your ID"
            value={data?.fullName || ''}
            onChange={(e) => handleChange('fullName', e?.target?.value)}
            style={inputStyle}
            onFocus={(e) => (e.target.style.borderColor = '#14B8A6')}
            onBlur={(e) => (e.target.style.borderColor = errors?.fullName ? '#f87171' : 'rgba(255,255,255,0.1)')}
          />
        </FormField>
        <FormField label="Date of Birth" required error={errors?.dob}>
          <input
            type="date"
            value={data?.dob || ''}
            onChange={(e) => handleChange('dob', e?.target?.value)}
            style={{ ...inputStyle, colorScheme: 'dark' }}
            onFocus={(e) => (e.target.style.borderColor = '#14B8A6')}
            onBlur={(e) => (e.target.style.borderColor = errors?.dob ? '#f87171' : 'rgba(255,255,255,0.1)')}
          />
        </FormField>
      </div>
      {/* Nationality + Phone */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Nationality" required error={errors?.nationality}>
          <input
            type="text"
            placeholder="e.g. American, British"
            value={data?.nationality || ''}
            onChange={(e) => handleChange('nationality', e?.target?.value)}
            style={inputStyle}
            onFocus={(e) => (e.target.style.borderColor = '#14B8A6')}
            onBlur={(e) => (e.target.style.borderColor = errors?.nationality ? '#f87171' : 'rgba(255,255,255,0.1)')}
          />
        </FormField>
        <FormField label="Phone Number" required error={errors?.phone}>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="+1"
              value={data?.phoneCode || ''}
              onChange={(e) => handleChange('phoneCode', e?.target?.value)}
              style={{ ...inputStyle, width: '70px', flexShrink: 0 }}
              onFocus={(e) => (e.target.style.borderColor = '#14B8A6')}
              onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
            />
            <input
              type="tel"
              placeholder="Phone number"
              value={data?.phone || ''}
              onChange={(e) => handleChange('phone', e?.target?.value)}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = '#14B8A6')}
              onBlur={(e) => (e.target.style.borderColor = errors?.phone ? '#f87171' : 'rgba(255,255,255,0.1)')}
            />
          </div>
        </FormField>
      </div>
      {/* Address */}
      <div
        className="p-4 rounded-xl space-y-4"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div className="flex items-center gap-2 mb-1">
          <Icon name="MapPin" size={14} color="#14B8A6" />
          <span className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>Residential Address</span>
        </div>
        <FormField label="Street Address" required error={errors?.street}>
          <input
            type="text"
            placeholder="123 Main Street, Apt 4B"
            value={data?.address?.street || ''}
            onChange={(e) => handleAddressChange('street', e?.target?.value)}
            style={inputStyle}
            onFocus={(e) => (e.target.style.borderColor = '#14B8A6')}
            onBlur={(e) => (e.target.style.borderColor = errors?.street ? '#f87171' : 'rgba(255,255,255,0.1)')}
          />
        </FormField>
        <div className="grid grid-cols-2 gap-4">
          <FormField label="City" required error={errors?.city}>
            <input
              type="text"
              placeholder="City"
              value={data?.address?.city || ''}
              onChange={(e) => handleAddressChange('city', e?.target?.value)}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = '#14B8A6')}
              onBlur={(e) => (e.target.style.borderColor = errors?.city ? '#f87171' : 'rgba(255,255,255,0.1)')}
            />
          </FormField>
          <FormField label="State / Province" required={false} error={errors?.state}>
            <input
              type="text"
              placeholder="State"
              value={data?.address?.state || ''}
              onChange={(e) => handleAddressChange('state', e?.target?.value)}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = '#14B8A6')}
              onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
            />
          </FormField>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Postcode / ZIP" required error={errors?.postcode}>
            <input
              type="text"
              placeholder="10001"
              value={data?.address?.postcode || ''}
              onChange={(e) => handleAddressChange('postcode', e?.target?.value)}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = '#14B8A6')}
              onBlur={(e) => (e.target.style.borderColor = errors?.postcode ? '#f87171' : 'rgba(255,255,255,0.1)')}
            />
          </FormField>
          <FormField label="Country" required error={errors?.country}>
            <input
              type="text"
              placeholder="United States"
              value={data?.address?.country || ''}
              onChange={(e) => handleAddressChange('country', e?.target?.value)}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = '#14B8A6')}
              onBlur={(e) => (e.target.style.borderColor = errors?.country ? '#f87171' : 'rgba(255,255,255,0.1)')}
            />
          </FormField>
        </div>
      </div>
      {/* Employment + Income */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Employment Status" required error={errors?.employmentStatus}>
          <select
            value={data?.employmentStatus || ''}
            onChange={(e) => handleChange('employmentStatus', e?.target?.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
            onFocus={(e) => (e.target.style.borderColor = '#14B8A6')}
            onBlur={(e) => (e.target.style.borderColor = errors?.employmentStatus ? '#f87171' : 'rgba(255,255,255,0.1)')}
          >
            <option value="" style={{ background: '#1e293b' }}>Select status</option>
            {EMPLOYMENT_OPTIONS?.map((opt) => (
              <option key={opt?.value} value={opt?.value} style={{ background: '#1e293b' }}>
                {opt?.label}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Annual Income Range" required error={errors?.incomeRange}>
          <select
            value={data?.incomeRange || ''}
            onChange={(e) => handleChange('incomeRange', e?.target?.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
            onFocus={(e) => (e.target.style.borderColor = '#14B8A6')}
            onBlur={(e) => (e.target.style.borderColor = errors?.incomeRange ? '#f87171' : 'rgba(255,255,255,0.1)')}
          >
            <option value="" style={{ background: '#1e293b' }}>Select range</option>
            {INCOME_OPTIONS?.map((opt) => (
              <option key={opt?.value} value={opt?.value} style={{ background: '#1e293b' }}>
                {opt?.label}
              </option>
            ))}
          </select>
        </FormField>
      </div>
    </div>
  );
};

export default PersonalInfoStep;
